import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Button,
  TextInput,
  Table,
  Box,
  Avatar,
  Loader,
  Center,
  Badge,
  ActionIcon,
  Menu,
  Select,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { Plus, Search, User, MoreVertical, Settings, Download } from 'react-feather';
import { usersApi, type InternalUserDetails, type UserStatus } from '../../../services/api/users';
import { authService } from '../../../services/api/auth';
import { AddUserModal } from '../../../components/users/AddUserModal';
import { UserDetailsDrawer } from '../../../components/users/UserDetailsDrawer';
import { SortableTableHeader, Pagination, ColumnSettingsPopover, ExportModal, EmptyState } from '../../../components/common';
import { useColumnConfig, type ColumnConfig } from '../../../hooks';
import { useAuthStore } from '../../../stores/auth';
import classes from './UserManagementPage.module.css';

type StatusFilter = UserStatus;

// Default column configuration for users table
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'name', label: 'User', visible: true, sortable: true },
  { id: 'persona', label: 'Persona', visible: true, sortable: false },
  { id: 'role', label: 'Role', visible: true, sortable: true },
  { id: 'department', label: 'Department', visible: true, sortable: true },
  { id: 'status', label: 'Status', visible: true, sortable: true },
  { id: 'last_login', label: 'Last Active', visible: true, sortable: true },
];

// Status filter pill options
const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Persona filter options
const PERSONA_FILTER_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'internal_admin', label: 'Admin' },
  { value: 'internal_user', label: 'Cohort Manager' },
];

// Get initials from user
function getUserInitials(user: InternalUserDetails): string {
  const first = user.first_name?.[0] || '';
  const last = user.last_name?.[0] || '';
  return (first + last).toUpperCase() || user.email[0].toUpperCase();
}

// Get display name
function getUserDisplayName(user: InternalUserDetails): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  }
  return user.email;
}

// Format date for display
function formatLastActive(dateString: string | null): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function UserManagementPage() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<InternalUserDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<StatusFilter>('all');
  const [selectedPersonaFilter, setSelectedPersonaFilter] = useState<string>('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [totalInactive, setTotalInactive] = useState(0);
  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);
  const [selectedUser, setSelectedUser] = useState<InternalUserDetails | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  // Use the reusable column config hook
  const {
    columns,
    setColumns,
    sortConfig,
    handleSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    visibleColumnCount,
  } = useColumnConfig({
    preferenceKey: 'user_management_settings',
    defaultColumns: DEFAULT_COLUMNS,
    defaultPageSize: 25,
  });

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      const response = await usersApi.list({
        search: debouncedSearch || undefined,
        status: selectedStatusFilter,
        persona: selectedPersonaFilter as 'internal_admin' | 'internal_user' | undefined || undefined,
        sortBy: sortConfig?.key || 'first_name',
        sortOrder: (sortConfig?.order as 'ASC' | 'DESC') || 'ASC',
        page,
        limit: pageSize,
      });

      setUsers(response.data);
      setTotalCount(response.pagination.total);
      setTotalActive(response.totalActive);
      setTotalInactive(response.totalInactive);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to fetch users',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedStatusFilter, selectedPersonaFilter, sortConfig, page, pageSize]);

  // Fetch users when filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle user created
  const handleUserCreated = () => {
    closeAddModal();
    fetchUsers();
    notifications.show({
      title: 'Success',
      message: 'User created successfully. Invitation email sent.',
      color: 'green',
    });
  };

  // Handle user updated
  const handleUserUpdated = () => {
    fetchUsers();
    notifications.show({
      title: 'Success',
      message: 'User updated successfully.',
      color: 'green',
    });
  };

  // Handle row click - open details drawer
  const handleUserClick = (user: InternalUserDetails) => {
    setSelectedUser(user);
    openDrawer();
  };

  // Handle deactivate user
  const handleDeactivate = async (user: InternalUserDetails) => {
    // Don't allow deactivating yourself
    if (user.id === currentUser?.id) {
      notifications.show({
        title: 'Error',
        message: 'You cannot deactivate your own account',
        color: 'red',
      });
      return;
    }

    modals.openConfirmModal({
      title: 'Deactivate User',
      children: (
        <Text size="sm">
          Are you sure you want to deactivate {getUserDisplayName(user)}?
        </Text>
      ),
      labels: { confirm: 'Deactivate', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await usersApi.deactivate(user.id);
          fetchUsers();
          notifications.show({
            title: 'Success',
            message: 'User deactivated successfully.',
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to deactivate user',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle activate user
  const handleActivate = async (user: InternalUserDetails) => {
    modals.openConfirmModal({
      title: 'Activate User',
      children: (
        <Text size="sm">
          Are you sure you want to reactivate {getUserDisplayName(user)}?
        </Text>
      ),
      labels: { confirm: 'Activate', cancel: 'Cancel' },
      confirmProps: { color: 'green' },
      onConfirm: async () => {
        try {
          await usersApi.activate(user.id);
          fetchUsers();
          notifications.show({
            title: 'Success',
            message: 'User activated successfully.',
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to activate user',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle resend invitation
  const handleResendInvitation = (user: InternalUserDetails) => {
    modals.openConfirmModal({
      title: 'Resend Invitation',
      children: (
        <Text size="sm">
          Resend the invitation email to {getUserDisplayName(user)}?
        </Text>
      ),
      labels: { confirm: 'Resend', cancel: 'Cancel' },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        try {
          await usersApi.resendInvitation(user.id);
          fetchUsers();
          notifications.show({
            title: 'Success',
            message: 'Invitation resent successfully.',
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to resend invitation',
            color: 'red',
          });
        }
      },
    });
  };

  // Handle reset password (admin-initiated)
  const handleResetPassword = (user: InternalUserDetails) => {
    modals.openConfirmModal({
      title: 'Reset Password',
      children: (
        <Text size="sm">
          Send a password reset email to {getUserDisplayName(user)} ({user.email})?
        </Text>
      ),
      labels: { confirm: 'Send Reset Email', cancel: 'Cancel' },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        try {
          await authService.adminResetPassword(user.id);
          notifications.show({
            title: 'Success',
            message: 'Password reset email sent successfully.',
            color: 'green',
          });
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to send reset email',
            color: 'red',
          });
        }
      },
    });
  };

  // Render persona badge
  const renderPersonaBadge = (user: InternalUserDetails) => {
    const isAdmin = user.persona === 'internal_admin';
    return (
      <Badge
        variant={isAdmin ? 'filled' : 'outline'}
        color={isAdmin ? 'gray' : 'medleyGreen'}
        size="sm"
        style={{
          textTransform: 'none',
          fontWeight: 500,
        }}
      >
        {user.persona_display}
      </Badge>
    );
  };

  // Render status badge
  const renderStatusBadge = (isActive: boolean) => {
    return (
      <Badge
        tt="uppercase"
        size="xs"
        variant={isActive ? 'filled' : 'outline'}
        color={isActive ? 'medleyGreen' : 'gray'}
        style={{
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.3px',
        }}
      >
        {isActive ? 'ACTIVE' : 'INACTIVE'}
      </Badge>
    );
  };

  // Render column header
  const renderColumnHeader = (column: ColumnConfig) => {
    if (column.sortable) {
      return (
        <Table.Th key={column.id} className={classes.tableHeader}>
          <SortableTableHeader
            label={column.label.toUpperCase()}
            sortKey={column.id === 'role' ? 'job_title' : column.id === 'last_login' ? 'last_login_at' : column.id === 'name' ? 'first_name' : column.id}
            currentSort={sortConfig}
            onSort={handleSort}
          />
        </Table.Th>
      );
    }
    return (
      <Table.Th key={column.id} className={classes.tableHeader}>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
          {column.label}
        </Text>
      </Table.Th>
    );
  };

  // Render cell content based on column id
  const renderCellContent = (column: ColumnConfig, user: InternalUserDetails) => {
    switch (column.id) {
      case 'name':
        return (
          <Group gap="sm" wrap="nowrap">
            <Avatar
              size={36}
              radius={36}
              color="medleyGreen"
              variant="filled"
              src={user.avatar_url}
            >
              {getUserInitials(user)}
            </Avatar>
            <Box>
              <Text fw={500} size="sm">
                {getUserDisplayName(user)}
              </Text>
              <Text size="xs" c="dimmed">
                {user.email}
              </Text>
            </Box>
          </Group>
        );
      case 'persona':
        return renderPersonaBadge(user);
      case 'role':
        return <Text size="sm">{user.job_title || '—'}</Text>;
      case 'department':
        return <Text size="sm">{user.department || '—'}</Text>;
      case 'status':
        return renderStatusBadge(user.is_active);
      case 'last_login':
        return <Text size="sm" c="dimmed">{formatLastActive(user.last_login_at)}</Text>;
      default:
        return <Text size="sm">—</Text>;
    }
  };

  // Get filter counts for display
  const getFilterCount = (status: StatusFilter): number => {
    if (status === 'all') return totalActive + totalInactive;
    if (status === 'active') return totalActive;
    return totalInactive;
  };

  // Empty state (no users at all)
  const hasFilters = debouncedSearch || selectedStatusFilter !== 'all' || selectedPersonaFilter;
  if (!loading && users.length === 0 && !hasFilters) {
    return (
      <Container size="xl" py="xl">
        <Title order={1} mb="xl">
          User Management
        </Title>
        <EmptyState
          icon={<User size={40} strokeWidth={1.5} />}
          title="No users yet"
          description="Get started by adding your first internal user to the system."
          action={
            <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
              Add User
            </Button>
          }
          size="lg"
          withCard
        />

        <AddUserModal opened={addModalOpened} onClose={closeAddModal} onSuccess={handleUserCreated} />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={1}>User Management</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {totalActive + totalInactive} users · {totalActive} active
          </Text>
        </Box>
        <Group gap="sm">
          <Button variant="default" leftSection={<Download size={18} />} onClick={openExportModal}>
            Export
          </Button>
          <Button leftSection={<Plus size={18} />} onClick={openAddModal}>
            Add User
          </Button>
        </Group>
      </Group>

      {/* Search and Filter Panel */}
      <Card withBorder p="md" mb="lg" className={classes.filterPanel} styles={{ root: { boxShadow: 'none' } }}>
        <Group justify="space-between" wrap="nowrap">
          {/* Left: Search and Filter Pills */}
          <Group gap="md" wrap="nowrap">
            <TextInput
              placeholder="Search users..."
              leftSection={<Search size={16} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              className={classes.searchInput}
            />

            {/* Status Filter Pills */}
            <Group gap={4}>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={selectedStatusFilter === option.value ? 'filled' : 'outline'}
                  color={selectedStatusFilter === option.value ? 'medleyGreen' : 'gray'}
                  size="xs"
                  className={classes.filterPill}
                  onClick={() => {
                    setSelectedStatusFilter(option.value);
                    setPage(1);
                  }}
                >
                  {option.label} ({getFilterCount(option.value)})
                </Button>
              ))}
            </Group>
          </Group>

          {/* Right: Persona Filter */}
          <Select
            placeholder="All Roles"
            data={PERSONA_FILTER_OPTIONS}
            value={selectedPersonaFilter}
            onChange={(value) => {
              setSelectedPersonaFilter(value || '');
              setPage(1);
            }}
            w={160}
            size="sm"
            clearable
          />
        </Group>
      </Card>

      {/* Content */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <Card withBorder p={0} styles={{ root: { boxShadow: 'none' } }}>
          <Table.ScrollContainer minWidth={900}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  {columns
                    .filter((col) => col.visible)
                    .map((col) => renderColumnHeader(col))}
                  {/* Actions column */}
                  <Table.Th className={classes.actionsColumn}>
                    <ColumnSettingsPopover
                      columns={columns}
                      onColumnsChange={setColumns}
                      trigger={
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <Settings size={16} />
                        </ActionIcon>
                      }
                    />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => (
                  <Table.Tr
                    key={user.id}
                    className={classes.tableRow}
                    onClick={() => handleUserClick(user)}
                  >
                    {columns
                      .filter((col) => col.visible)
                      .map((col) => (
                        <Table.Td key={col.id}>
                          {renderCellContent(col, user)}
                        </Table.Td>
                      ))}
                    {/* Actions menu */}
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Menu shadow="md" width={160} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <MoreVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item onClick={() => handleUserClick(user)}>
                            View Details
                          </Menu.Item>
                          {!user.email_verified_at && user.is_active && (
                            <Menu.Item
                              color="blue"
                              onClick={() => handleResendInvitation(user)}
                            >
                              Resend Invitation
                            </Menu.Item>
                          )}
                          {user.email_verified_at && (
                            <Menu.Item
                              onClick={() => handleResetPassword(user)}
                            >
                              Reset Password
                            </Menu.Item>
                          )}
                          {user.is_active ? (
                            <Menu.Item
                              color="red"
                              disabled={user.id === currentUser?.id}
                              onClick={() => handleDeactivate(user)}
                            >
                              Deactivate User
                            </Menu.Item>
                          ) : (
                            <Menu.Item
                              color="green"
                              onClick={() => handleActivate(user)}
                            >
                              Activate User
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {users.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={visibleColumnCount + 1}>
                      <Text ta="center" c="dimmed" py="xl">
                        No users found matching your filters
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalCount > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </Card>
      )}

      {/* Add User Modal */}
      <AddUserModal opened={addModalOpened} onClose={closeAddModal} onSuccess={handleUserCreated} />

      {/* User Details Drawer */}
      <UserDetailsDrawer
        opened={drawerOpened}
        onClose={() => {
          closeDrawer();
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUpdate={handleUserUpdated}
        onDeactivate={() => selectedUser && handleDeactivate(selectedUser)}
        onActivate={() => selectedUser && handleActivate(selectedUser)}
        onResendInvitation={() => selectedUser && handleResendInvitation(selectedUser)}
        currentUserId={currentUser?.id}
      />

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="users"
        recordCount={totalCount}
        filterContext={{
          search: debouncedSearch || undefined,
          status: selectedStatusFilter === 'all' ? undefined : selectedStatusFilter,
          persona: selectedPersonaFilter || undefined,
        }}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        filterSummary={[
          ...(debouncedSearch ? [{ label: 'Search', value: debouncedSearch }] : []),
          ...(selectedStatusFilter !== 'all' ? [{ label: 'Status', value: selectedStatusFilter }] : []),
          ...(selectedPersonaFilter ? [{ label: 'Role', value: PERSONA_FILTER_OPTIONS.find(o => o.value === selectedPersonaFilter)?.label ?? selectedPersonaFilter }] : []),
        ]}
      />
    </Container>
  );
}

export default UserManagementPage;
